import { useCallback, useMemo, useState } from 'react';
import { Scissors, Volume2, RotateCcw, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TimelineItem, VideoItem, AudioItem } from '@/types/timeline';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import { trimAudioSilence } from '@/features/timeline/stores/timeline-actions';
import { useGizmoStore } from '@/features/preview/stores/gizmo-store';
import { useProjectStore } from '@/features/projects/stores/project-store';
import { useSettingsStore } from '@/features/settings/stores/settings-store';
import { toast } from 'sonner';
import {
  PropertySection,
  PropertyRow,
  NumberInput,
} from '../components';
import { getMixedValue } from '../utils';
import { useTranslation } from '@/features/i18n/locales';
import { measureTrimmedAudioFrames } from '@/lib/audio/trim-silence';
import { cenemaProjectMeta } from '@/utils/cenema-mapper';
import { regenerateVoiceoversWithSpeed } from '@/features/editor/utils/voiceover-regeneration';

interface AudioSectionProps {
  items: TimelineItem[];
}

type AudioCapableItem = VideoItem | AudioItem;

/**
 * Audio section - volume and audio fades.
 * Shown for video and audio clips.
 */
export function AudioSection({ items }: AudioSectionProps) {
  const t = useTranslation();
  const updateItem = useTimelineStore((s) => s.updateItem);
  const timelineItems = useTimelineStore((s) => s.items);
  const fps = useTimelineStore((s) => s.fps);
  const currentProject = useProjectStore((s) => s.currentProject);
  const [isTrimmingSelection, setIsTrimmingSelection] = useState(false);
  const [isTrimmingVoiceover, setIsTrimmingVoiceover] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenSpeed, setRegenSpeed] = useState<number>(cenemaProjectMeta.ttsSpeed);

  // Gizmo store for live audio preview
  const setPropertiesPreviewNew = useGizmoStore((s) => s.setPropertiesPreviewNew);
  const clearPreview = useGizmoStore((s) => s.clearPreview);

  const audioItems = useMemo(
    () =>
      items.filter(
        (item): item is AudioCapableItem =>
          item.type === 'video' || item.type === 'audio'
      ),
    [items]
  );

  const itemIds = useMemo(() => audioItems.map((item) => item.id), [audioItems]);
  const selectedAudioOnly = useMemo(
    () => items.filter((item): item is AudioItem => item.type === 'audio'),
    [items]
  );
  const voiceoverItems = useMemo(
    () =>
      timelineItems.filter(
        (item): item is AudioItem =>
          item.type === 'audio' && item.label.trim().toLowerCase() === 'voiceover'
      ),
    [timelineItems]
  );

  // Get current values (volume in dB, defaults to 0 dB = unity gain)
  const volume = getMixedValue(audioItems, (item) => item.volume, 0);
  const fadeIn = getMixedValue(audioItems, (item) => item.audioFadeIn, 0);
  const fadeOut = getMixedValue(audioItems, (item) => item.audioFadeOut, 0);

  // Live preview for volume (during drag)
  const handleVolumeLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { volume: number }> = {};
      itemIds.forEach((id) => {
        previews[id] = { volume: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit volume (on mouse up)
  // Update store first, then clear preview to avoid flicker
  const handleVolumeChange = useCallback(
    (value: number) => {
      itemIds.forEach((id) => updateItem(id, { volume: value }));
      // Defer preview clear to next microtask so store update propagates first
      queueMicrotask(() => clearPreview());
    },
    [itemIds, updateItem, clearPreview]
  );

  // Live preview for audio fade in (during drag)
  const handleFadeInLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { audioFadeIn: number }> = {};
      itemIds.forEach((id) => {
        previews[id] = { audioFadeIn: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit audio fade in (on mouse up)
  const handleFadeInChange = useCallback(
    (value: number) => {
      itemIds.forEach((id) => updateItem(id, { audioFadeIn: value }));
      queueMicrotask(() => clearPreview());
    },
    [itemIds, updateItem, clearPreview]
  );

  // Live preview for audio fade out (during drag)
  const handleFadeOutLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { audioFadeOut: number }> = {};
      itemIds.forEach((id) => {
        previews[id] = { audioFadeOut: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit audio fade out (on mouse up)
  const handleFadeOutChange = useCallback(
    (value: number) => {
      itemIds.forEach((id) => updateItem(id, { audioFadeOut: value }));
      queueMicrotask(() => clearPreview());
    },
    [itemIds, updateItem, clearPreview]
  );

  // Reset volume to 0 dB
  // Read current values from store to avoid depending on audioItems (prevents callback recreation)
  const handleResetVolume = useCallback(() => {
    const tolerance = 0.1;
    const currentItems = useTimelineStore.getState().items;
    const needsUpdate = currentItems.some(
      (item) => itemIds.includes(item.id) && Math.abs(item.volume ?? 0) > tolerance
    );
    if (needsUpdate) {
      itemIds.forEach((id) => updateItem(id, { volume: 0 }));
    }
  }, [itemIds, updateItem]);

  // Reset audio fade in to 0
  const handleResetFadeIn = useCallback(() => {
    const tolerance = 0.01;
    const currentItems = useTimelineStore.getState().items;
    const needsUpdate = currentItems.some(
      (item) => itemIds.includes(item.id) && (item.audioFadeIn ?? 0) > tolerance
    );
    if (needsUpdate) {
      itemIds.forEach((id) => updateItem(id, { audioFadeIn: 0 }));
    }
  }, [itemIds, updateItem]);

  // Reset audio fade out to 0
  const handleResetFadeOut = useCallback(() => {
    const tolerance = 0.01;
    const currentItems = useTimelineStore.getState().items;
    const needsUpdate = currentItems.some(
      (item) => itemIds.includes(item.id) && (item.audioFadeOut ?? 0) > tolerance
    );
    if (needsUpdate) {
      itemIds.forEach((id) => updateItem(id, { audioFadeOut: 0 }));
    }
  }, [itemIds, updateItem]);

  const handleRegenAllVoiceover = useCallback(async () => {
    const targets = voiceoverItems.filter((item) => !!(item as any).sceneText);
    if (targets.length === 0) {
      toast.warning('재생성할 보이스오버가 없습니다. (sceneText 없음)');
      return;
    }
    const confirmed = window.confirm(
      `전체 보이스오버 ${targets.length}개를 ${regenSpeed}x로 재생성할까요?\n기존 음성은 새 음성으로 교체됩니다.`
    );
    if (!confirmed) return;
    setIsRegenerating(true);
    try {
      const result = await regenerateVoiceoversWithSpeed({
        project: currentProject,
        voiceovers: targets as any,
        fps,
        speed: regenSpeed,
        autoTrimSilence: useSettingsStore.getState().autoTrimVoiceoverSilence,
        updateItem: (id, patch) => updateItem(id, patch as any),
      });
      toast.success(
        result.trimmedCount > 0
          ? `보이스오버 ${result.regeneratedCount}개 재생성 후 무음 제거까지 반영했습니다.`
          : `보이스오버 ${result.regeneratedCount}개 재생성 완료! (${regenSpeed}x)`
      );
    } catch (e: any) {
      console.error('[RegenVoiceover]', e);
      toast.error(`재생성 실패: ${e.message}`);
    } finally {
      setIsRegenerating(false);
    }
  }, [currentProject, voiceoverItems, regenSpeed, fps, updateItem]);

  const runSilenceTrim = useCallback(
    async (targets: AudioItem[], setBusy: (value: boolean) => void, successMessage: string) => {
      if (targets.length === 0) {
        toast.warning('무음 제거할 보이스오버가 없습니다.');
        return;
      }

      setBusy(true);
      try {
        const measured = await Promise.all(
          targets.map(async (item) => {
            const result = await measureTrimmedAudioFrames(item.src, fps);
            if (!result) return null;
            
            // Check if there's any actual silence to trim
            if (result.leadingSilenceFrames + result.trailingSilenceFrames < 1) return null;
            
            return { 
              id: item.id, 
              durationInFrames: result.durationInFrames,
              leadingSilenceFrames: result.leadingSilenceFrames
            };
          })
        );

        const updates = measured.filter(
          (entry): entry is { id: string; durationInFrames: number; leadingSilenceFrames: number } => Boolean(entry)
        );

        if (updates.length === 0) {
          toast.message('줄일 무음 구간이 없습니다.');
          return;
        }

        trimAudioSilence(updates);
        toast.success(successMessage);
      } catch (error) {
        console.error('Failed to trim audio silence', error);
        toast.error('무음 제거 중 오류가 발생했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [fps]
  );

  const handleTrimSelection = useCallback(() => {
    void runSilenceTrim(selectedAudioOnly, setIsTrimmingSelection, '선택한 오디오의 무음을 제거했습니다.');
  }, [runSilenceTrim, selectedAudioOnly]);

  const handleTrimAllVoiceover = useCallback(() => {
    void runSilenceTrim(voiceoverItems, setIsTrimmingVoiceover, '전체 보이스오버의 무음을 제거했습니다.');
  }, [runSilenceTrim, voiceoverItems]);

  if (audioItems.length === 0) return null;

  return (
    <PropertySection title={t.properties.audio} icon={Volume2} defaultOpen={true}>
      {/* Volume in dB (-60 to +20, 0 dB = unity gain) */}
      <PropertyRow label={t.properties.gain}>
        <div className="flex items-center gap-1 w-full">
          <NumberInput
            value={volume}
            onChange={handleVolumeChange}
            onLiveChange={handleVolumeLiveChange}
            min={-60}
            max={20}
            step={0.1}
            unit="dB"
            className="flex-1 min-w-0"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={handleResetVolume}
            title={t.properties.resetToZero}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>

      {/* Audio Fades */}
      <PropertyRow label={t.properties.fadeIn}>
        <div className="flex items-center gap-1 w-full">
          <NumberInput
            value={fadeIn}
            onChange={handleFadeInChange}
            onLiveChange={handleFadeInLiveChange}
            min={0}
            max={5}
            step={0.1}
            unit="s"
            className="flex-1 min-w-0"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={handleResetFadeIn}
            title={t.properties.resetToZero}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>

      <PropertyRow label={t.properties.fadeOut}>
        <div className="flex items-center gap-1 w-full">
          <NumberInput
            value={fadeOut}
            onChange={handleFadeOutChange}
            onLiveChange={handleFadeOutLiveChange}
            min={0}
            max={5}
            step={0.1}
            unit="s"
            className="flex-1 min-w-0"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={handleResetFadeOut}
            title={t.properties.resetToZero}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>

      <PropertyRow label="무음 제거">
        <div className="flex w-full flex-col gap-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={handleTrimSelection}
            disabled={selectedAudioOnly.length === 0 || isTrimmingSelection || isTrimmingVoiceover}
          >
            <Scissors className="h-3.5 w-3.5" />
            {isTrimmingSelection ? '선택 클립 정리 중...' : '선택 오디오 무음 제거'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={handleTrimAllVoiceover}
            disabled={voiceoverItems.length === 0 || isTrimmingSelection || isTrimmingVoiceover}
          >
            <Scissors className="h-3.5 w-3.5" />
            {isTrimmingVoiceover ? '전체 보이스오버 정리 중...' : '전체 보이스오버 무음 제거'}
          </Button>
        </div>
      </PropertyRow>

      <PropertyRow label="보이스오버 재생성">
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">배속</span>
            <NumberInput
              value={regenSpeed}
              onChange={setRegenSpeed}
              min={0.5}
              max={2.0}
              step={0.05}
              unit="x"
              className="flex-1 min-w-0"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={handleRegenAllVoiceover}
            disabled={voiceoverItems.length === 0 || isRegenerating}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
            {isRegenerating ? '재생성 중...' : `전체 보이스오버 재생성 (${regenSpeed}x)`}
          </Button>
        </div>
      </PropertyRow>
    </PropertySection>
  );
}
